import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { MsalService } from '@azure/msal-angular';

import { environment } from '../../../../environments/environment';
import { AuthStateService } from './auth-state.service';
import { AuthenticatedUser, Permission } from '../models';
import { UserContextService } from './user-context.service';
import { LocaleService } from '../../services/locale.service';

@Injectable({
  providedIn: 'root'
})
export class AuthSessionService {
  private readonly authStateService = inject(AuthStateService);
  private readonly msalService = inject(MsalService);
  private readonly userContextService = inject(UserContextService);
  private readonly localeService = inject(LocaleService);

  private isInitialized = false;
  private initializePromise: Promise<void> | null = null;
  private msalInitialized = false;
  private redirectError = false;
  private loginAttempted = false;
  private externalRedirectStarted = false;

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializePromise) {
      return this.initializePromise;
    }

    this.initializePromise = this.performInitialize();

    try {
      await this.initializePromise;
    } finally {
      this.initializePromise = null;
    }
  }

  private async performInitialize(): Promise<void> {
    this.authStateService.beginBootstrap();
    const urlLocale = this.localeService.getLocaleFromUrl();

    if (!environment.auth.enabled) {
      this.isInitialized = true;
      this.authStateService.completeBootstrap(this.getFallbackUser(), this.getFallbackPermissions());
      return;
    }

    await this.ensureMsalInitialized();
    await this.ensureRedirectHandled();

    const account = this.resolveActiveAccount();

    if (!account) {
      this.isInitialized = true;
      this.authStateService.clearSession();
      this.localeService.clearPreferredLocale();
      return;
    }

    if (this.redirectIfRoleContainsIn(this.mapAccountToUser(account))) {
      this.isInitialized = true;
      return;
    }

    this.loginAttempted = false;

    const storedPreference = this.localeService.getStoredPreferredLocale();
    const backendContext = await this.userContextService.loadUserAndPermissions();

    if (backendContext) {
      const preferredLocale = this.localeService.normalizePreferredLocale(backendContext.user.preferredLanguage)
        ?? this.localeService.getBrowserLocale();
      const pageLocale = storedPreference && urlLocale ? urlLocale : preferredLocale;
      this.localeService.setPreferredLocale(preferredLocale);
      this.isInitialized = true;

      if (this.redirectIfRoleContainsIn(backendContext.user)) {
        return;
      }

      this.authStateService.completeBootstrap(backendContext.user, backendContext.permissions);
      if (!this.localeService.usesUnprefixedLocalRoutes() && this.localeService.getLocaleFromPath() !== pageLocale) {
        this.redirectToLocale(pageLocale);
      }
      return;
    }

    const preferredLocale = storedPreference ?? this.localeService.getBrowserLocale();
    this.localeService.setPreferredLocale(preferredLocale);
    this.isInitialized = true;
    this.authStateService.completeBootstrap(this.mapAccountToUser(account), []);
    const pageLocale = storedPreference && urlLocale ? urlLocale : preferredLocale;
    if (!this.localeService.usesUnprefixedLocalRoutes() && this.localeService.getLocaleFromPath() !== pageLocale) {
      this.redirectToLocale(pageLocale);
    }
  }

  private redirectToLocale(locale: string): void {
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(this.localeService.buildLocalizedAbsoluteUrl(locale, currentUrl));
  }

  isAuthEnabled(): boolean {
    return environment.auth.enabled;
  }

  isAuthenticated(): boolean {
    return this.authStateService.isAuthenticated();
  }

  shouldFailToUnauthorized(): boolean {
    return this.redirectError || this.loginAttempted;
  }

  hasExternalRedirectStarted(): boolean {
    return this.externalRedirectStarted;
  }

  async login(): Promise<void> {
    if (!environment.auth.enabled) {
      return;
    }

    await this.ensureMsalInitialized();

    this.loginAttempted = true;
    const browserLocale = this.localeService.getBrowserLocale();
    if (!this.localeService.usesUnprefixedLocalRoutes() && this.localeService.getLocaleFromPath() !== browserLocale) {
      this.redirectToLocale(browserLocale);
      return;
    }

    this.msalService.loginRedirect({
      scopes: [...environment.auth.scopes],
      redirectUri: this.localeService.buildLocalizedAbsoluteUrl(browserLocale, environment.auth.redirectUri),
      redirectStartPage: this.localeService.buildLocalizedAbsoluteUrl(
        browserLocale,
        `${window.location.pathname}${window.location.search}${window.location.hash}`
      )
    });
  }

  signOut(): void {
    const account = this.resolveActiveAccount();
    const logoutHint = this.resolveLogoutHint(account?.idTokenClaims, account?.username);

    this.authStateService.clearSession();
    this.loginAttempted = false;
    this.redirectError = false;
    this.externalRedirectStarted = false;
    this.isInitialized = false;

    this.localeService.clearPreferredLocale();

    if (!environment.auth.enabled) {
      void this.initialize();
      return;
    }

    this.msalService.logoutRedirect({
      logoutHint,
      postLogoutRedirectUri: this.localeService.buildLocalizedAbsoluteUrl(
        this.localeService.getCurrentLocale(),
        environment.auth.postLogoutRedirectUri
      )
    });
  }

  private resolveLogoutHint(idTokenClaims?: Record<string, unknown>, fallbackUsername?: string): string | undefined {
    const loginHintClaim = idTokenClaims?.['login_hint'];
    if (typeof loginHintClaim === 'string' && loginHintClaim.trim().length > 0) {
      return loginHintClaim;
    }

    const preferredUsernameClaim = idTokenClaims?.['preferred_username'];
    if (typeof preferredUsernameClaim === 'string' && preferredUsernameClaim.trim().length > 0) {
      return preferredUsernameClaim;
    }

    if (typeof fallbackUsername === 'string' && fallbackUsername.trim().length > 0) {
      return fallbackUsername;
    }

    return undefined;
  }

  private resolveActiveAccount() {
    const currentAccount = this.msalService.instance.getActiveAccount();

    if (currentAccount) {
      return currentAccount;
    }

    const allAccounts = this.msalService.instance.getAllAccounts();

    if (!allAccounts.length) {
      return null;
    }

    this.msalService.instance.setActiveAccount(allAccounts[0]);
    return allAccounts[0];
  }

  private async ensureMsalInitialized(): Promise<void> {
    if (this.msalInitialized) {
      return;
    }

    await this.msalService.instance.initialize();
    this.msalInitialized = true;
  }

  private async ensureRedirectHandled(): Promise<void> {
    const hadAuthResponseInUrl = this.hasAuthResponseInUrl();

    try {
      const redirectResult = await firstValueFrom(this.msalService.handleRedirectObservable());

      if (redirectResult?.account) {
        this.msalService.instance.setActiveAccount(redirectResult.account);
        this.redirectError = false;
        return;
      }

      const activeAccount = this.resolveActiveAccount();
      if (hadAuthResponseInUrl && !activeAccount) {
        this.redirectError = true;
      }
    } catch {
      this.redirectError = true;
    }
  }

  private hasAuthResponseInUrl(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    const hashValue = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const hashParams = new URLSearchParams(hashValue);

    return this.isMsalAuthResponse(searchParams) || this.isMsalAuthResponse(hashParams);
  }

  private isMsalAuthResponse(params: URLSearchParams): boolean {
    const hasState = params.has('state');
    const hasCode = params.has('code');
    const hasError = params.has('error') || params.has('error_description');
    const hasTokenMarkers = params.has('id_token') || params.has('client_info') || params.has('session_state');

    // MSAL responses always include state; requiring it prevents false positives from extension query/hash params.
    return hasState && (hasCode || hasError || hasTokenMarkers);
  }

  private mapAccountToUser(account: {
    localAccountId: string;
    name?: string | null;
    username: string;
    idTokenClaims?: Record<string, unknown>;
  }): AuthenticatedUser {
    const roleClaims = account.idTokenClaims?.['roles'];
    const roles = Array.isArray(roleClaims) ? roleClaims.filter((value): value is string => typeof value === 'string') : [];

    const preferredLanguage = this.localeService.resolvePreferredLocale();

    return {
      userId: account.localAccountId,
      displayName: account.name ?? account.username,
      role: roles[0] ?? null,
      roles,
      email: account.username,
      phoneNumber: null,
      preferredLanguage
    };
  }

  private redirectIfRoleContainsIn(user: AuthenticatedUser): boolean {
    const roleValues = [user.role, ...(user.roles ?? [])]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.toLowerCase());

    const hasInRole = roleValues.some((role) => role.includes('redirect'));
    if (!hasInRole) {
      return false;
    }

    const targetUrl = environment.externalLinks.shipSso;
    if (!this.isSafeExternalRedirectTarget(targetUrl)) {
      return false;
    }

    this.externalRedirectStarted = true;
    this.navigateToExternalUrl(targetUrl);
    return true;
  }

  private navigateToExternalUrl(targetUrl: string): void {
    window.location.assign(targetUrl);
  }

  private isSafeExternalRedirectTarget(targetUrl: string): boolean {
    try {
      const parsedUrl = new URL(targetUrl, window.location.origin);
      const isHttpProtocol = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
      const isExternalOrigin = parsedUrl.origin !== window.location.origin;

      return isHttpProtocol && isExternalOrigin;
    } catch {
      return false;
    }
  }

  private getFallbackUser(): AuthenticatedUser {
    return {
      userId: 'dev-user',
      displayName: 'Development User',
      role: 'Admin',
      roles: ['Admin'],
      email: 'dev.user@mymidland.local',
      phoneNumber: '555 555 5555',
      preferredLanguage: 'en-ca'
    };
  }

  private getFallbackPermissions(): Permission[] {
    return [
      { slug: 'statement_billing' },
      { slug: 'user_management' },
      { slug: 'shipment_access' }
    ];
  }

  public checkUserPermissions(requiredPermissions: string[]): boolean {
    const userPermissions = JSON.parse(sessionStorage.getItem('user_perms') || '[]');    
    return requiredPermissions.every((requiredPermission) => userPermissions.includes(requiredPermission)); 
  }
}